import React, { useEffect, useRef, useState } from "react";
import { Grid, IconButton, Button, CircularProgress, Typography, TextField, makeStyles, Box, MenuItem } from "@material-ui/core";
import { Field, FieldProps, Form, Formik, FormikProps, getIn } from "formik";
import CloseIcon from '@material-ui/icons/Close';
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import * as Yup from 'yup';
import { useDispatch, useSelector } from "react-redux";
import { DateTime as d } from "luxon";
import { updateAuthStateAction } from "../../../actions/auth/actions";
import { AuthStates, ICustomer } from "../../../reducers/auth/types";
import { IAppState } from "../../../store";
import { CREATE_CUSTOMER } from "../../../graphql/customers/createCustomerMutation";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { getDefaultCountryCode, getLocalisedLicense, getLocalizedDateFormat } from "../../../utils/localized.syntex";
import AddressPicker from "../../AddressPicker/AddressPicker";
import { IAddress, IIdentityConfiguration } from "../../../reducers/bookings/types";
import { useSnackBar } from "../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../SnackbarWrapper/SnackbarWrapper";
import { buildFieldRequiredMap, buildFieldVisibilityMap, DATE_TYPE, defaultWebsiteIndividualFields, formatGraphQLErrorMessage } from "../../utils";
import { useTranslation } from "react-i18next";
import { GET_CUSTOM_FORM_BY_TYPE } from "../../../graphql/customers/getConsumerCustomForm";
import { TranslationKeys } from "../../../utils/translations/types";
import FlatPickerBar from "../../FlatPicker";
import { NameTitleOptions } from "../NewBooking/utils";
import { Gender } from "../../../reducers/drivers/const";
import { EntrustSdk } from "../../IdentityVerification/EntrustSdk";
import { hasIdvCredits } from "../../IdentityVerification/utils";
import { START_IDENTITY_VERIFICATION_PROCESS } from "../../../graphql/identityVerification/entrustIdentityVerificationMutation";
import { GET_CONSUMER_CUSTOMER_BY_APPLICANT_ID } from "../../../graphql/customers/getConsumerCustomerByApplicant";
import { socket } from "../../../utils/socket";
import { SOCKET_EVENTS } from "../utils";
import { GET_CONSUMER_CUSTOMER } from "../../../graphql/customers/getConsumerCustomer";
import { create } from "lodash";
import { IdentityVerificationStatus } from "../../../reducers/website/types";
import { GET_IDV_CONFIGURATION } from "../../../graphql/user/getIdvConfigurationQuery";

const useStyles = makeStyles((theme) => ({
  formContainer: {
    maxWidth: "100%",
    margin: "0 auto",
    padding: "10px 20px",
  },
  header: {
    paddingBottom: theme.spacing(3),
    borderBottom: "1px solid #f0f0f0",
    marginBottom: theme.spacing(4),
  },
  phoneWrapper: {
    position: "relative",
    "& .PhoneInput": {
      display: "flex",
      alignItems: "center",
      height: "40px",
      borderRadius: 4,
      border: "1px solid rgba(0, 0, 0, 0.23)",
      transition: "border-color 200ms",
      backgroundColor: "#fff",
      "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)" },
      "&:focus-within": {
        borderColor: theme.palette.primary.main,
        borderWidth: "2px",
      },
    },
    "& .PhoneInputCountry": {
      marginRight: "10px",
      display: "flex",
      alignItems: "center",
    },
    "& .PhoneInputCountryIcon": {
      width: "24px",
      height: "16px",
      borderRadius: "2px",
    },
    "& .PhoneInputInput": {
      border: "none !important",
      outline: "none",
      fontSize: "14px",
      width: "100%",
      height: "38px",
      background: "transparent",
    },
  },
  errorBorder: {
    "& .PhoneInput": { borderColor: "#D13135 !important" }
  },
  label: {
    fontSize: "12px",
    color: "#666",
    marginBottom: "4px",
    display: "block",
    fontWeight: 500
  },

  verificationWrapper: {
    width: "100%",
    maxWidth: 460,
    margin: "0 auto",
    padding: theme.spacing(3, 2),
    boxSizing: "border-box",

    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 0),
    },
  },

  verificationHeader: {
    textAlign: "center",
  },

  verificationIcon: {
    width: 64,
    height: 64,
    margin: "0 auto 20px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,

    "& svg": {
      fontSize: 32,
    },

    [theme.breakpoints.down("xs")]: {
      width: 56,
      height: 56,
      marginBottom: 16,

      "& svg": {
        fontSize: 28,
      },
    },
  },

  verificationTitle: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,

    [theme.breakpoints.down("xs")]: {
      fontSize: 21,
    },
  },

  verificationDescription: {
    marginTop: theme.spacing(1),
    fontSize: 15,
    lineHeight: 1.6,
    color: theme.palette.text.secondary,

    [theme.breakpoints.down("xs")]: {
      fontSize: 14,
    },
  },

  verificationActions: {
    marginTop: theme.spacing(4),
  },

  verifyButton: {
    minHeight: 48,
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    textTransform: "none",
  },

  otherDeviceButton: {
    minHeight: 48,
    marginTop: theme.spacing(1.5),
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    textTransform: "none",
  },

  skipButton: {
    display: "block",
    margin: `${theme.spacing(1)}px auto 0`,
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
    textTransform: "none",

    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.text.primary,
    },
  },
}));

const initialRegistartionValues = {
  title: "",
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phoneNumber: {
    country: "",
    phone: ""
  },
  secondaryPhoneNumber: { phone: "", country: "" },
  secondaryEmail: "",
  dateOfBirth: "",
  gender: "",
  profession: "",
  nationality: "",
  passportNumber: "",
  customerPCONumber: "",
  pcoExpiryDate: "",
  pcoIssueDate: "",
  accountNumber: "",
  nationalInsuranceNumber: "",

  location: {
    city: "",
    country: "",
    fullAddress: "",
    state: "",
    street: "",
    zipcode: ""
  },
  license: {
    licenseNumber: "",
    authority: "",
    dateOfExpiry: "",
    dateOfIssue: ""
  },
}

type RegistrationStep =
  | "registration"
  | "verification"
  | "success";

enum IdentityVerificationFlow {
  SDK = "SDK",
  SMART_CAPTURE = "SMART_CAPTURE",
}

export const AuthRegister: React.FC = () => {

  const dispatch = useDispatch();

  const { t } = useTranslation();
  const classes = useStyles();
  const snackbar = useSnackBar();

  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer);
  const organisationData = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);

  const { identityVerification } = website.consumerWebsite.tenancy;
  const { country } = website.consumerWebsite.organisation.address;
  const isMultiOrganisation = website.consumerWebsite.isMultiOrganisation || false

  const [countryCode, setCountry] = useState<any>(getDefaultCountryCode(country));
  const [isRegistered, setIsRegistered] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [secPhoneError, setSecPhoneError] = useState("");
  const [requiredMap, setRequiredMap] = useState<Record<string, boolean>>(buildFieldRequiredMap(defaultWebsiteIndividualFields));
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(buildFieldVisibilityMap(defaultWebsiteIndividualFields));
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>("registration");
  const [createdCustomer, setCreatedCustomer] = useState<ICustomer | null>(null);
  const [openEntrustSdk, setOpenEntrustSdk] = useState<boolean>(false);
  const [sdkToken, setSdkToken] = useState<string>("");
  const [workflowRunId, setWorkflowRunId] = useState<string>("");
  const [applicantId, setApplicantId] = useState<string>("");
  const [isPollingVerification, setIsPollingVerification] = useState(false);
  const [idvConfiguration, setIdvConfiguration] = useState<IIdentityConfiguration | null>()
  const sdkOpenedRef = useRef(false);

  const [createCustomer, { loading: creatingCustomer }] = useMutation(CREATE_CUSTOMER, {
    onCompleted: ({ consumerCreateCustomer }) => {

      setCreatedCustomer(consumerCreateCustomer);

      if (
        identityVerification?.enabled &&
        idvConfiguration?.verificationPoints?.customerRegistration?.enabled &&
        hasIdvCredits(idvConfiguration)
      ) {
        setRegistrationStep("verification");
        return;
      }

      setRegistrationStep("success");
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  const [startIdentityVerificationOnDevice, { loading: idvLoading, data: idvData }] = useMutation(START_IDENTITY_VERIFICATION_PROCESS, {
    onCompleted: (data) => {
      if (data?.startIdentityVerification) {
        if (sdkOpenedRef.current) return;

        sdkOpenedRef.current = true;

        setSdkToken(data.startIdentityVerification.sdkToken);
        setApplicantId(data.startIdentityVerification.applicantId);
        setWorkflowRunId(data.startIdentityVerification.workflowRunId)
        setOpenEntrustSdk(true);
      }
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      })
  });

  const [startIdentityVerificationProcessOnAnotherDevice, { loading: idvAnotherDeviceLoading, data: idvAnotherDeviceData }] = useMutation(START_IDENTITY_VERIFICATION_PROCESS, {
    onCompleted: (data) => {
      setRegistrationStep("success");
      return snackbar({
        message: "Email sent to your registered email address. Please check your email to complete the verification process.",
        variant: SnackBarVariant.SUCCESS
      });
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      })
  });

  const [getCustomerByApplicantId, { loading: getCustomerLoading, data: getCustomerData }] = useLazyQuery(GET_CONSUMER_CUSTOMER_BY_APPLICANT_ID, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const verification = data.consumerGetCustomerByApplicantId.identityVerification
      if (!verification) {
        return;
      }

      const { status } = verification;

      if (
        status === IdentityVerificationStatus.APPROVED ||
        status === IdentityVerificationStatus.REJECTED ||
        status === IdentityVerificationStatus.IN_REVIEW
      ) {
        sdkOpenedRef.current = false;
        setIsPollingVerification(false);
        setRegistrationStep("success");
        setOpenEntrustSdk(false);
      }
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      })
  });

  const [getCustomFormByType] = useLazyQuery(GET_CUSTOM_FORM_BY_TYPE, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {

      if (data?.getConsumerCustomFormType?.config?.website?.fields.length) {
        const customMap = buildFieldRequiredMap(data.getConsumerCustomFormType.config.website.fields);
        const customVisibilityMap = buildFieldVisibilityMap(data.getConsumerCustomFormType.config.website.fields);
        setRequiredMap(customMap);
        setVisibilityMap(customVisibilityMap);
      } else {
        const defaultMap = buildFieldRequiredMap(defaultWebsiteIndividualFields);
        const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteIndividualFields);
        setRequiredMap(defaultMap);
        setVisibilityMap(defaultVisibilityMap);
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      const defaultMap = buildFieldRequiredMap(defaultWebsiteIndividualFields);
      const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteIndividualFields);
      setRequiredMap(defaultMap);
      setVisibilityMap(defaultVisibilityMap);
    }
  });

  const [loadIdvConfiguration, { loading: loadingIdvConfiguration, data: idvConfigData }] = useLazyQuery(
    GET_IDV_CONFIGURATION,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data.getIdvConfiguration) {
          setIdvConfiguration(data.getIdvConfiguration)
        }
      },
      onError: (err) => {

      }
    }
  );

  useEffect(() => {
    getCustomFormByType({
      variables: {
        organisationId: organisationData?.id,
        formType: "INDIVIDUAL",
        atBranchLevel: false
      }
    })
  }, [])


  useEffect(() => {
    if (!isPollingVerification || !applicantId) return;
    const poll = () => {

      getCustomerByApplicantId({
        variables: { applicantId }
      });
    };

    poll();

    const interval = setInterval(poll, 3000);

    return () => clearInterval(interval);
  }, [isPollingVerification, applicantId]);

  useEffect(() => {
    if (identityVerification) {
      loadIdvConfiguration({
        variables: {
          configurationId: identityVerification.id
        }
      })
    }
  }, [identityVerification])

  const tt = (key: string, options?: any): string => {
    return (t(key as any, options) as unknown as string) || key;
  };

  const createDynamicCustomerSchema = (
    requiredMap: Record<string, boolean>,
    t: (key: string, options?: Record<string, any>) => string
  ) => {
    const getLabel = (labelKey: string) => t(labelKey) ?? labelKey;

    const createConditionalString = (
      key: string,
      errorKey: TranslationKeys,
      interpolationValues?: Record<string, string | number>
    ) =>
      requiredMap[key]
        ? interpolationValues
          ? Yup.string().required(t(errorKey, { ...interpolationValues }))
          : Yup.string().required(t(errorKey))
        : Yup.string();

    const createConditionalEmail = (fieldKey: string, labelKey: string) => {
      const base = Yup.string().email(
        t("validation:pleaseEnterAValidEmail", { field: getLabel(labelKey) })
      );
      return requiredMap[fieldKey]
        ? base.required(
          t("validation:emailIsRequired", { field: getLabel(labelKey) })
        )
        : base;
    };

    const createConditionalPhone = (fieldKey: string, labelKey: string) => {
      const base = Yup.string().test(
        "is-valid-phone",
        t("root:invalidPhoneNumber"),
        (value) => {
          if (!value) return true;
          return isValidPhoneNumber(value);
        }
      );

      return requiredMap[fieldKey]
        ? base.required(
          t("validation:phoneNumberRequired", {
            field: getLabel(labelKey),
          })
        )
        : base;
    };

    const requiredString = (key: string, label: string) => {
      return requiredMap[key]
        ? Yup.string().required(tt("validation:required", { field: tt(label) }))
        : Yup.string().nullable();
    };

    const addressSchema = () => {
      if (!requiredMap.location) {
        return Yup.object({
          fullAddress: Yup.string(),
          country: Yup.string(),
          state: Yup.string(),
          city: Yup.string(),
          street: Yup.string(),
          zipcode: Yup.string(),
        });
      }

      return Yup.object({
        fullAddress: Yup.string().required(tt("validation:required", { field: tt("root:address") })),
        country: Yup.string().required(tt("validation:required", { field: tt("root:country") })),
        state: Yup.string().required(tt("validation:required", { field: tt("root:state") })),
        city: Yup.string().required(tt("validation:required", { field: tt("root:city") })),
        street: Yup.string().required(tt("validation:required", { field: tt("root:street") })),
        zipcode: Yup.string(),
      });
    };

    return Yup.object().shape({
      title: requiredString(
        "title",
        "root:title"
      ),
      firstName: createConditionalString(
        "firstName",
        "validation:firstNameRequired"
      ),
      middleName: createConditionalString(
        "middleName",
        "validation:middleNameRequired"
      ),
      lastName: createConditionalString(
        "lastName",
        "validation:lastNameRequired"
      ),

      nationality: requiredString("nationality", "root:nationality"),


      email: createConditionalEmail("email", "root:emailAddress"),

      secondaryEmail: createConditionalEmail(
        "secondaryEmail",
        "root:secondaryEmailAddress"
      ),

      phoneNumber: Yup.object().shape({
        phone: createConditionalPhone("phoneNumber", "root:phoneNumber"),
        country: Yup.string(),
      }),


      secondaryPhoneNumber: Yup.object().shape({
        phone: createConditionalPhone(
          "secondaryPhoneNumber",
          "root:secondaryPhoneNumber"
        ),
        country: Yup.string(),
      }),

      location: addressSchema(),

      nationalInsuranceNumber: createConditionalString(
        "nationalInsuranceNumber",
        "validation:nationalInsuranceNumberRequired"
      ),

      passportNumber: requiredString(
        "passportNumber",
        "root:passportNumber"
      ),

      profession: createConditionalString(
        "profession",
        "validation:professionIsRequired"
      ),

      dateOfBirth: requiredString(
        "dateOfBirth",
        "root:dateOfBirth"
      ),

      gender: createConditionalString("gender", "validation:genderRequired"),

      customerPCONumber: createConditionalString(
        "customerPCONumber",
        "validation:customerPCONumberRequired"
      ),

      accountNumber: createConditionalString(
        "accountNumber",
        "validation:accountNumberRequired"
      ),

      license: Yup.object().shape({
        licenseNumber: createConditionalString(
          "licenseNumber",
          "validation:licenceNumberRequired",
          { licenceSyntax: getLocalisedLicense(country) }
        ),
        authority: createConditionalString(
          "authority",
          "validation:authorityRequired"
        ),
        dateOfIssue: requiredString(
          "dateOfIssue",
          "root:drivingLicenceIssueDate"
        ),
        dateOfExpiry: requiredString(
          "dateOfExpiry",
          "root:drivingLicenceExpiryDate"
        ),

      }),
      pcoIssueDate: requiredString(
        "pcoIssueDate",
        "root:pcoIssueDate"
      ),
      pcoExpiryDate: requiredString(
        "pcoExpiryDate",
        "root:pcoExpiryDate"
      ),
      orgCode: isMultiOrganisation ? Yup.string().required(`${t('validation:organisationCodeRequired')}`) : Yup.string().nullable()
    });
  };

  const schema = React.useMemo(() => createDynamicCustomerSchema(requiredMap, t), [requiredMap, t, country]);

  const ninetyFiveYearsOldDate = getLocalizedDateFormat(
    country,
    d.now().minus({ year: 95 }).toUTC().toISO(),
    DATE_TYPE.CONDENSED
  );

  const eighteenYearsOldDate = getLocalizedDateFormat(
    country,
    d.now().minus({ year: 18 }).toUTC().toISO(),
    DATE_TYPE.CONDENSED
  );

  const renderTextField = (
    name: string,
    configKey: string,
    label: TranslationKeys,
    placeholder: TranslationKeys,
    interpolation?: Record<string, any>,
    options?: string[]
  ) => {
    if (name != 'orgCode') {
      if (!visibilityMap[configKey]) return null;
    }


    const tOptions = interpolation || {};

    return (
      <Grid item xs={12} sm={6} md={4}>
        <Box mb={1}>
          <label className={classes.label}>
            {t(label, tOptions)}
            {requiredMap[configKey] && (
              <span style={{ color: "#D13135", marginLeft: 4, fontWeight: 600 }}>*</span>
            )}
          </label>

          <Field name={name}>
            {({ field, meta }: FieldProps) => (
              <TextField
                {...field}
                placeholder={t(placeholder, tOptions) as string}
                variant="outlined"
                fullWidth
                size="small"
                select={Boolean(options)}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error ? String(meta.error) : ""}
                SelectProps={{
                  native: false,
                }}
              >
                {options && options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Field>
        </Box>
      </Grid>
    );
  };

  const renderPhoneField = (
    name: string,
    configKey: string,
    label: TranslationKeys,
    formik: FormikProps<any>
  ) => {
    if (!visibilityMap[configKey]) return null;

    return (
      <Grid item xs={12} sm={6} md={4}>
        <Field name={`${name}.phone`}>
          {({ field, meta }: FieldProps) => {
            const showError = meta.touched && Boolean(meta.error);

            return (
              <Box mb={1}>
                <label
                  className={classes.label}
                  style={{ color: showError ? "#D13135" : "#666" }}
                >
                  {t(label)}
                  {requiredMap[configKey] && (
                    <span style={{ color: "#D13135", marginLeft: 4 }}>*</span>
                  )}
                </label>

                <div className={`${classes.phoneWrapper} ${showError ? classes.errorBorder : ""}`}>
                  <PhoneInput
                    international={false}
                    defaultCountry={formik.values[name]?.country || countryCode}
                    value={field.value}
                    onChange={(val: string) => {
                      formik.setFieldValue(`${name}.phone`, val || "");
                    }}
                    onCountryChange={(cc: any) => {
                      if (cc) {
                        formik.setFieldValue(`${name}.country`, cc);
                      }
                    }}
                    onBlur={() => {
                      formik.setFieldTouched(`${name}.phone`, true);
                    }}
                  />
                </div>

                {showError && (
                  <Typography variant="caption" style={{ color: "#D13135", marginLeft: 14 }}>
                    {meta.error}
                  </Typography>
                )}
              </Box>
            );
          }}
        </Field>
      </Grid>
    );
  };

  const renderDatePicker = (name: string, configKey: string, label: TranslationKeys, formik: FormikProps<any>, minDate?: string, maxDate?: string) => {
    if (!visibilityMap[configKey]) return null;

    const val = getIn(formik.values, name);
    const error = getIn(formik.errors, name);
    const touched = getIn(formik.touched, name);

    const translatedLabel = t(label) as string;

    return (
      <Grid item xs={12} sm={6} md={4}>
        <Box mb={1}>
          <label className={classes.label}>
            {translatedLabel} {requiredMap[configKey] && <span style={{ color: "#D13135" }}>*</span>}
          </label>
          <FlatPickerBar
            enableTime={false}
            handleDateChange={(value: Date | null) => {
              formik.setFieldValue(name, value ? d.fromJSDate(value).toUTC().toISO() : "");
            }}
            label={""}
            identifier={configKey}
            placeholderValue={translatedLabel}
            value={(val ? getLocalizedDateFormat(country, val, DATE_TYPE.CONDENSED) : "") as string}
            minDate={minDate}
            maxDate={maxDate}
            country={country}
          />
          {touched && error && (
            <Typography variant="caption" style={{ color: "#D13135", marginLeft: 14 }}>
              {String(error)}
            </Typography>
          )}
        </Box>
      </Grid>
    );
  };

  const handleVerify = (flow: IdentityVerificationFlow) => {

    if (!createdCustomer?.id) {
      return snackbar({
        message: "Customer ID is missing. Please try again.",
        variant: SnackBarVariant.ERROR
      });
    }

    if (flow === IdentityVerificationFlow.SDK) {
      startIdentityVerificationOnDevice({
        variables: {
          customerId: createdCustomer?.id,
          flow: IdentityVerificationFlow.SDK
        }
      })
    } else {
      startIdentityVerificationProcessOnAnotherDevice({
        variables: {
          customerId: createdCustomer?.id,
          flow: IdentityVerificationFlow.SMART_CAPTURE
        }
      })
    }
  };

  const onSkipVerification = () => {
    setRegistrationStep("success");
  }

  const renderRegistrationForm = () => {
    return (
      <>
        <Box display="flex" justifyContent="center" alignItems="center" className={classes.header} position="relative">
          <Typography variant="h2" style={{ fontWeight: 600, color: "#333" }}>{t("root:registerNow").toUpperCase()}</Typography>
          <IconButton style={{ position: 'absolute', right: -10, top: -10 }} onClick={() => dispatch(updateAuthStateAction({ state: AuthStates.SIGNED_OUT }))}><CloseIcon /></IconButton>
        </Box>

        <Formik
          initialValues={initialRegistartionValues}
          validationSchema={schema}
          onSubmit={(values) => {
            if (requiredMap.phoneNumber && !isValidPhoneNumber(values.phoneNumber.phone)) {
              setPhoneError(t("validation:enterValidPhoneNumber"));
              return;
            }
            createCustomer({ variables: { customer: values } });
          }}
        >
          {(formik) => (
            <Form>
              <Grid container spacing={3}>
                {isMultiOrganisation && renderTextField("orgCode", "orgCode", "root:organisationCode", "root:organisationCode")}
                {renderTextField("title", "title", "root:title", "root:title", {}, NameTitleOptions)}
                {renderTextField("firstName", "firstName", "root:firstNameLabel", "root:enterFirstName")}
                {renderTextField("middleName", "middleName", "root:middleNameLabel", "root:enterMiddleName")}
                {renderTextField("lastName", "lastName", "root:lastNameLabel", "root:enterLastName")}
                {renderTextField("gender", "gender", "root:gender", "root:gender", {}, Object.values(Gender))}

                {renderTextField("email", "email", "root:emailLabel", "root:enterYourMail")}
                {renderTextField("secondaryEmail", "secondaryEmail", "root:secondaryEmailAddress", "root:enterYourMail")}
                {renderPhoneField("phoneNumber", "phoneNumber", "root:phoneNumber", formik)}
                {renderPhoneField("secondaryPhoneNumber", "secondaryPhoneNumber", "root:secondaryPhoneNumber", formik)}
                {renderDatePicker("dateOfBirth", "dateOfBirth", "root:dateOfBirth", formik, ninetyFiveYearsOldDate, eighteenYearsOldDate)}
                {renderTextField("nationality", "nationality", "root:nationality", "root:nationality")}
                {renderTextField("profession", "profession", "root:profession", "root:enterYourProfession")}
                {renderTextField("passportNumber", "passportNumber", "root:passportNumber", "root:enterPassportNumber")}

                {renderTextField("license.licenseNumber", "licenseNumber", "root:licenceNumber", "root:enterLicenseNumber")}
                {renderTextField("license.authority", "authority", "root:licenceIssuedBy", "root:issuedBy", { licenceSyntax: getLocalisedLicense(country) })}
                {renderDatePicker("license.dateOfIssue", "dateOfIssue", "root:drivingLicenceIssueDate", formik, "", getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED))}
                {renderDatePicker("license.dateOfExpiry", "dateOfExpiry", "root:drivingLicenceExpiryDate", formik, getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED), "")}

                {renderTextField("customerPCONumber", "customerPCONumber", "root:pcoPrivateHiresHireDLNo", "root:pcoPrivateHiresHireDLNo")}
                {renderDatePicker("pcoIssueDate", "pcoIssueDate", "root:pcoIssueDate", formik, "", getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED))}
                {renderDatePicker("pcoExpiryDate", "pcoExpiryDate", "root:pcoExpiryDate", formik, getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED), "")}

                {renderTextField("nationalInsuranceNumber", "nationalInsuranceNumber", "root:nationalInsuranceNumber", "root:enterNationalInsuranceNumber")}
                {renderTextField("accountNumber", "accountNumber", "root:accountNumber", "root:accountNumber")}

                {visibilityMap.location && (
                  <Grid item xs={12}>
                    <Box mt={1}>
                      <AddressPicker
                        fieldName={"location"}
                        required={requiredMap.location}
                        onChange={(address: IAddress) => {
                          if (address && address.fullAddress) {
                            formik.setFieldValue('location', address)
                          }
                          else {
                            formik.setFieldValue("location", {
                              street: "",
                              city: "",
                              country: "",
                              fullAddress: "",
                              state: "",
                              zipcode: ""
                            })
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Box mt={6} textAlign="center">
                <Button variant="contained" type="submit" disabled={creatingCustomer} style={{ background: "#3A3A3A", color: "#fff", padding: "14px 100px", borderRadius: "8px" }}>
                  {creatingCustomer ? <CircularProgress size={24} color="inherit" /> : t("root:registerNow")}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </>
    )
  };

  const renderIdentityVerification = () => {
    return (
      <Box className={classes.verificationWrapper}>
        <Box className={classes.verificationHeader}>
          <Box className={classes.verificationIcon}>
            <VerifiedUserIcon />
          </Box>

          <Typography className={classes.verificationTitle}>
            Verify your identity
          </Typography>

          <Typography className={classes.verificationDescription}>
            Please verify your identity to complete your registration.
          </Typography>
        </Box>

        <Box className={classes.verificationActions}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className={classes.verifyButton}
            onClick={() => handleVerify(IdentityVerificationFlow.SDK)}
            disabled={idvLoading}
          >
            {idvLoading ? <CircularProgress size={24} color="inherit" /> : "Verify on this device"}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            fullWidth
            className={classes.otherDeviceButton}
            onClick={() => handleVerify(IdentityVerificationFlow.SMART_CAPTURE)}
            disabled={idvAnotherDeviceLoading}
          >
            {idvAnotherDeviceLoading ? <CircularProgress size={24} color="inherit" /> : "Use another device"}
          </Button>

          {
            idvConfiguration?.verificationPoints?.customerRegistration?.enabled &&
            !idvConfiguration?.verificationPoints?.customerRegistration?.required && (
              <Button
                variant="text"
                className={classes.skipButton}
                onClick={onSkipVerification}
              >
                Skip for now
              </Button>
            )
          }
        </Box>
      </Box>
    )
  }

  const renderRegistrationComplete = () => {
    return (
      <div style={{ maxWidth: 400, margin: "auto", padding: 16 }}>
        <Grid container style={{ padding: "0 48px", marginTop: 16 }}>
          <Grid item xs={12}>
            <h2 style={{ textAlign: "center", padding: 0, margin: 0 }}>{t("root:thankYou")}</h2>
          </Grid>
        </Grid>
        <div style={{ padding: "1 10%" }}>
          <p style={{}}>{t("root:onStartEmail")}</p>
          <p style={{}}>{t("root:emailNotSeen")}</p>
        </div>
        <div style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            style={{
              background: "#3A3A3A",
              marginTop: 20,
              boxShadow: "none",
              fontSize: 12,
              marginBottom: 16
            }}
            onClick={() => dispatch(updateAuthStateAction({ state: AuthStates.SIGNED_OUT }))}
          >
            {t("misc:signIn")}
          </Button>
        </div>
      </div>
    )
  }

  const renderRegistrationStep = () => {
    switch (registrationStep) {
      case 'registration':
        return renderRegistrationForm();

      case 'verification':
        return renderIdentityVerification();

      case 'success':
        return renderRegistrationComplete();

      default:
        return null;
    }
  };

  return (
    <div className={classes.formContainer}>
      {renderRegistrationStep()}
      {/* Entrust SDK */}
      {
        openEntrustSdk && (
          <EntrustSdk
            open={openEntrustSdk}
            sdkToken={sdkToken}
            workflowRunId={workflowRunId}
            loading={false}
            onComplete={() => {
              console.log("Entrust SDK completed");
              setIsPollingVerification(true);
            }}
            onClose={() => {
              sdkOpenedRef.current = false;
              setOpenEntrustSdk(false);
            }}
            onError={(error: any) => {
              console.error("Entrust SDK error", error);
              sdkOpenedRef.current = false;
              setOpenEntrustSdk(false);
            }}
          />
        )
      }
    </div>
  );
};